import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "types";
import { useAuth } from "aws/AuthContext";
import { synchronizeFetchExams } from "store/ExamStore/slice";
import { TypeExam } from "app/constants/types/exam.types";
import { TypeExamProps, TypeSortByExam } from "./types";
import { selectExamStore } from "store/ExamStore/slice/selector";
import { EXAMSUBJECT } from "app/constants/content";
import { AppConfig } from "app/config";

const useExam = ({ filterCountdown, getLength }: TypeExamProps) => {
  const dispatch = useDispatch();
  const examStore = useSelector(selectExamStore);
  const {
    loading,
    error,
    enrolledExamsLoading,
    enrolledExamsError,
    isSynchronizing,
    exams,
  } = examStore;
    console.log("🚀 ~ file: useExam.ts ~ line 23 ~ useExam ~ exams", exams)
  
  const isLoading = loading || enrolledExamsLoading || isSynchronizing;
  // Only consider main exam fetch error as blocking - enrolled exams error shouldn't block display
  const hasError = error;
  const [subject, setSubject] = React.useState("all");
  const [paper, setPaper] = React.useState("");
  const [level, setLevel] = React.useState("all");
  const [searchQuery, setSearchQuery] = React.useState("");
  const [sortBy, setSortBy] = React.useState<TypeSortByExam>("status");
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [statusFilter, setStatusFilter] = React.useState<string[]>([]);

  
  const { user, isLoading: isAuthLoaded } = useAuth();
  const uid = user?.userId;
  const isAuthEmpty = !user;

  const filterList = (data: TypeExam[]): TypeExam[] => {
    try {
      let result = [...data];
      // Filter by subject with multiple matching strategies
      if (subject && subject !== "all") {
        result = result.filter((item: TypeExam) => {
          if (!item.subject) return false;

          // Get the display name for the selected subject key
          const selectedSubjectName = EXAMSUBJECT[subject];

          // Try multiple matching strategies
          return (
            item.subject === selectedSubjectName ||
            item.subject.toLowerCase() === selectedSubjectName.toLowerCase() ||
            // Check if subject contains the selected subject name (for partial matches)
            item.subject
              .toLowerCase()
              .includes(selectedSubjectName.toLowerCase()) ||
            // For Chinese specifically, try additional check
            (subject === "chinese" &&
              (item.subject === "Chinese" ||
                item.subject === "chinese" ||
                item.subject.includes("Chinese") ||
                item.subject.includes("chinese")))
          );
        });
      }

      if (level && level !== "all") {
        result = result.filter((item: TypeExam) => item.level === level);
      }

      if (statusFilter && statusFilter.length > 0) {
        result = result.filter((item: TypeExam) =>
          statusFilter.includes(item.status || "")
        );
      }

      // Filter by search query
      if (searchQuery) {
        result = result.filter(
          (item: TypeExam) =>
            item.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.description
              ?.toLowerCase()
              .includes(searchQuery.toLowerCase()) ||
            item.examTime?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            item.examDuration?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      // Filter out exams before the configured cutoff date
      const cutoffDate = new Date(AppConfig.EXAM_CUTOFF_DATE).getTime();
      result = result.filter((item: TypeExam) => {
        const examTime = item.rawData?.examTime;
        if (!examTime) return true;
        return examTime >= cutoffDate;
      });

      // Filter out 2024-2025 academic year exams if flag is set to false
      if (!AppConfig.SHOW_2425_EXAMS) {
        // Academic year: Sept 1, 2024 - Aug 31, 2025
        const academicYearStart = new Date("2024-09-01T00:00:00").getTime();
        const academicYearEnd = new Date("2025-08-31T23:59:59").getTime();

        result = result.filter((item: TypeExam) => {
          // Use rawData.examTime which is the unix timestamp
          const examTime = item.rawData?.examTime;
          if (!examTime) return true; // Keep exams without date info

          // Filter out exams that fall within the 2024-2025 academic year
          return examTime < academicYearStart || examTime > academicYearEnd;
        });
      }

      // Sort by specified field
      if (sortBy) {
        result = result.sort((a: TypeExam, b: TypeExam) => {
          if (sortBy === "examTime") {
            return (
              new Date(b[sortBy as keyof object] || "").getTime()
              -
              new Date(a[sortBy as keyof object] || "").getTime() 
            );
          } else if (sortBy === "status") {
            // Define status priority order matching TypeExamStatus
            const statusPriority: { [key: string]: number } = {
              "ongoing": 1,
              "open": 2,
              "standby-open": 3,
              "countdown": 4,
              "standby": 5,
              "standby-locked": 6,
              "not-enrolled": 7,
              "submitted": 8,
              "ended": 9,
              "close": 10,
              "graded": 11,
              "missing": 12,
              "pending": 13
            };
            
            const aStatus = a.status || "";
            const bStatus = b.status || "";
            const aPriority = statusPriority[aStatus] || 999;
            const bPriority = statusPriority[bStatus] || 999;
            
            // Primary sort by status priority
            if (aPriority !== bPriority) {
              return aPriority - bPriority;
            }
            
            // Secondary sort by exam date DESC (newer dates first)
            return (
              new Date(b.examTime || "").getTime() - 
              new Date(a.examTime || "").getTime()
            );
          } else {
            const aValue = (a[sortBy as keyof object] as string) || "";
            const bValue = (b[sortBy as keyof object] as string) || "";
            return bValue.localeCompare(aValue);
          }
        });
      }

      return result;
    } catch (err) {
      console.error("Error filtering exams:", err);
      return data || [];
    }
  };

  React.useEffect(() => {
    dispatch(synchronizeFetchExams());

    // Set up polling interval with synchronized action
    const interval = setInterval(() => {
      dispatch(synchronizeFetchExams());
    }, 15000); // 15 seconds

    return () => clearInterval(interval);
  }, [dispatch]);

  
  let dataExamOnView = filterList(exams);

  // Apply countdown filter if needed
  if (filterCountdown) {
    dataExamOnView = dataExamOnView.filter(
      (item: TypeExam) =>
        item.status === "standby-locked" ||
        item.status === "standby-open" ||
        item.status === "open" ||
        item.status === "countdown" ||
        item.status === "standby"
    );
  }

  
  if (getLength) {
    getLength(dataExamOnView.length);
  }

  return {
    subject,
    paper,
    level,
    searchQuery,
    sortBy,
    isExpanded,
    statusFilter,
    isAuthLoaded,
    isAuthEmpty,
    uid,
    setSubject,
    setPaper,
    setLevel,
    setSearchQuery,
    setSortBy,
    setIsExpanded,
    setStatusFilter,
    dataExam: dataExamOnView,
    isLoading,
    hasError,
  };
};

export default useExam;
